/**
 * V2.0.44 — Wall Adjacency Graph (P1 Phase 1).
 *
 * PURE utility — no React, no DOM, no side effects. Given a blueprint
 * floor, returns the list of shared-wall segments that the upcoming
 * drag-resize UI will hook onto.
 *
 * A "wall handle" represents a single contiguous wall segment that
 * can be dragged perpendicular to its axis. Each handle has:
 *   - id           : stable string ("v:x=5.0:z=2..7")
 *   - axis         : "x" (vertical wall, runs along z) or "z" (horizontal)
 *   - coord        : the x or z coordinate of the wall plane
 *   - lo, hi       : the extent along the perpendicular axis
 *   - rooms        : Set of room ids that share this wall
 *   - is_exterior  : true iff exactly one room is on either side
 *
 * The handle list is intentionally OVER-segmented: if three rooms
 * share the same wall plane at different z ranges, we emit one
 * handle per contiguous span. The drag-resize UI uses this so that
 * dragging the kitchen-bedroom wall doesn't accidentally move the
 * bedroom-bathroom wall.
 *
 * This module is the foundation for Phases 2 (pointer math + clamps)
 * and 3 (preview-channel updates + door/window shifting).
 */

const EPS = 0.001;

import {
  IRC_DOOR_MIN_M,
  IRC_WINDOW_MIN_M,
  EDGE_MARGIN_M,
  adaDoorTargetWidth,
} from "./openingCodes";

function _safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function _activeRooms(floor) {
  return (floor?.rooms || []).filter(
    (r) => r && r.deleted !== true && _safeNum(r.width) > 0 && _safeNum(r.depth) > 0,
  );
}

/**
 * Returns a list of vertical (axis="x") wall handles AND horizontal
 * (axis="z") wall handles for the given floor.
 *
 * @param {object} floor — blueprint floor with { rooms: [...] }
 * @returns {Array} handles
 */
export function computeWallHandles(floor) {
  const rooms = _activeRooms(floor);
  if (rooms.length === 0) return [];

  // For each axis, collect "events" per coord — each room contributes
  // two events per axis. Verticals at room.x and room.x+width with
  // span [z, z+depth]. Horizontals at room.z and room.z+depth with
  // span [x, x+width].
  const verticals = new Map(); // coord(string) -> [{lo,hi,room_id}]
  const horizontals = new Map();

  for (const r of rooms) {
    const x = _safeNum(r.x);
    const z = _safeNum(r.z);
    const w = _safeNum(r.width);
    const d = _safeNum(r.depth);
    const id = r.id;
    const kX1 = x.toFixed(3);
    const kX2 = (x + w).toFixed(3);
    const kZ1 = z.toFixed(3);
    const kZ2 = (z + d).toFixed(3);
    if (!verticals.has(kX1)) verticals.set(kX1, []);
    if (!verticals.has(kX2)) verticals.set(kX2, []);
    verticals.get(kX1).push({ lo: z, hi: z + d, room_id: id });
    verticals.get(kX2).push({ lo: z, hi: z + d, room_id: id });
    if (!horizontals.has(kZ1)) horizontals.set(kZ1, []);
    if (!horizontals.has(kZ2)) horizontals.set(kZ2, []);
    horizontals.get(kZ1).push({ lo: x, hi: x + w, room_id: id });
    horizontals.get(kZ2).push({ lo: x, hi: x + w, room_id: id });
  }

  const handles = [];
  for (const [coordKey, segs] of verticals.entries()) {
    handles.push(..._segsToHandles("x", parseFloat(coordKey), segs));
  }
  for (const [coordKey, segs] of horizontals.entries()) {
    handles.push(..._segsToHandles("z", parseFloat(coordKey), segs));
  }
  return handles;
}

/**
 * Given many [lo, hi] room contributions on a single wall plane,
 * splits them into contiguous overlap segments. Each output handle
 * carries the set of rooms that touch that span — so a single
 * 3-room wall produces up to 5 handles (one per change in adjacency).
 */
function _segsToHandles(axis, coord, segs) {
  // Build a sorted event list of (point, type, room_id). type=+1 enter, -1 leave.
  const events = [];
  for (const s of segs) {
    events.push({ p: s.lo, kind: 1, room_id: s.room_id });
    events.push({ p: s.hi, kind: -1, room_id: s.room_id });
  }
  events.sort((a, b) => a.p - b.p || b.kind - a.kind);

  const active = new Set();
  const out = [];
  let prev = null;
  for (const e of events) {
    if (prev !== null && Math.abs(e.p - prev) > EPS && active.size > 0) {
      const rooms = new Set(active);
      out.push({
        id: `${axis}:${coord.toFixed(3)}:${prev.toFixed(3)}-${e.p.toFixed(3)}`,
        axis,
        coord,
        lo: prev,
        hi: e.p,
        rooms,
        is_exterior: rooms.size === 1,
      });
    }
    if (e.kind === 1) active.add(e.room_id);
    else active.delete(e.room_id);
    prev = e.p;
  }
  return out;
}

/**
 * Returns the centre-point of a handle in world coordinates.
 * Used by the SVG layer to position the draggable hit-target circle.
 */
export function handleCentre(h) {
  const mid = (h.lo + h.hi) / 2;
  return h.axis === "x"
    ? { x: h.coord, z: mid }
    : { x: mid, z: h.coord };
}

// V2.0.45 — P1 Phase 2 — Drag math.
//
// Clamps a proposed wall move so that every adjacent room stays at
// least MIN_ROOM_DIM metres on the dragged axis. Returns the
// effective delta after clamping. Caller multiplies the raw pointer
// delta by this and rebuilds the blueprint.

const MIN_ROOM_DIM = 1.5; // metres
const GRID_SNAP = 0.1;    // metres — keeps dimensions clean

function _snap(v) {
  return Math.round(v / GRID_SNAP) * GRID_SNAP;
}

/**
 * Returns the maximum allowed wall delta given the current handle +
 * blueprint. Rooms on the LEFT/BOTTOM of the wall plane (their
 * far edge IS the wall) can shrink down to MIN_ROOM_DIM. Rooms on
 * the RIGHT/TOP (their NEAR edge is the wall) can shrink similarly.
 *
 * Positive delta = move wall toward +x or +z. Returns {min, max}.
 */
export function clampRangeForHandle(handle, floor) {
  const rooms = (floor?.rooms || []).filter(
    (r) => r && r.deleted !== true,
  );
  let lo = -Infinity, hi = Infinity;
  for (const r of rooms) {
    if (!handle.rooms.has(r.id)) continue;
    if (handle.axis === "x") {
      const farX = Number(r.x) + Number(r.width);
      if (Math.abs(farX - handle.coord) < 0.01) {
        // wall is the +x edge of this room ⇒ moving negative shrinks it
        lo = Math.max(lo, MIN_ROOM_DIM - Number(r.width));
      } else if (Math.abs(Number(r.x) - handle.coord) < 0.01) {
        // wall is the -x edge ⇒ moving positive shrinks it
        hi = Math.min(hi, Number(r.width) - MIN_ROOM_DIM);
      }
    } else {
      const farZ = Number(r.z) + Number(r.depth);
      if (Math.abs(farZ - handle.coord) < 0.01) {
        lo = Math.max(lo, MIN_ROOM_DIM - Number(r.depth));
      } else if (Math.abs(Number(r.z) - handle.coord) < 0.01) {
        hi = Math.min(hi, Number(r.depth) - MIN_ROOM_DIM);
      }
    }
  }
  if (!Number.isFinite(lo)) lo = -50;
  if (!Number.isFinite(hi)) hi = 50;
  return { min: lo, max: hi };
}

/**
 * V2.0.46 — Phase 3 — also shifts attached openings so doors and
 * windows stay glued to their wall.
 *
 * V2.0.47 — Phase 4 — also auto-shrinks opening widths when the host
 * wall is contracting; blocks the drag entirely if shrinking would
 * push an opening below the applicable code minimum. The signature
 * remains `applyWallDrag(blueprint, floorIndex, handle, delta, opts?)`.
 *
 * Pure — does not mutate the input.
 *
 * Return shape: `{ blueprint, warnings, blocked }` where
 *   - `blueprint` is the new (or unchanged on block) blueprint.
 *   - `warnings` is an array of `{ type, kind, id, room_name?, width?, min? }`.
 *   - `blocked` is `true` iff the move was rejected.
 */
export function applyWallDrag(blueprint, floorIndex, handle, delta, opts = {}) {
  const adaMode = !!opts.adaMode;
  if (!blueprint || !handle || Math.abs(delta) < 1e-6) {
    return { blueprint: blueprint || null, warnings: [], blocked: false };
  }
  const out = JSON.parse(JSON.stringify(blueprint));
  const floor = (out.floors || []).find(
    (f) => Number(f.floor_index ?? f.level) === Number(floorIndex),
  );
  if (!floor) return { blueprint, warnings: [], blocked: false };

  // 1) Snapshot pre-move geometry so we can preserve absolute centres
  //    for openings on the affected rooms' perpendicular walls.
  const prevRooms = new Map();
  for (const r of floor.rooms || []) {
    if (!r || r.deleted === true) continue;
    prevRooms.set(r.id, {
      x: Number(r.x), z: Number(r.z),
      width: Number(r.width), depth: Number(r.depth),
      name: r.name,
    });
  }
  // 1b) For each interior door pair, capture pre-move centre along its
  //     shared wall — used to freeze position into explicit form below.
  const preDoorCentres = _captureInteriorDoorCentres(floor, prevRooms);

  // 2) Apply the wall move to rooms.
  for (const r of floor.rooms || []) {
    if (!r || r.deleted === true) continue;
    if (!handle.rooms.has(r.id)) continue;
    if (handle.axis === "x") {
      const farX = Number(r.x) + Number(r.width);
      if (Math.abs(farX - handle.coord) < 0.01) {
        // wall is the +x edge — grow / shrink width
        r.width = _snap(Math.max(MIN_ROOM_DIM, Number(r.width) + delta));
      } else if (Math.abs(Number(r.x) - handle.coord) < 0.01) {
        // wall is the -x edge — shift x AND adjust width
        const newX = _snap(Number(r.x) + delta);
        const dx = newX - Number(r.x);
        r.x = newX;
        r.width = _snap(Math.max(MIN_ROOM_DIM, Number(r.width) - dx));
      }
    } else {
      const farZ = Number(r.z) + Number(r.depth);
      if (Math.abs(farZ - handle.coord) < 0.01) {
        r.depth = _snap(Math.max(MIN_ROOM_DIM, Number(r.depth) + delta));
      } else if (Math.abs(Number(r.z) - handle.coord) < 0.01) {
        const newZ = _snap(Number(r.z) + delta);
        const dz = newZ - Number(r.z);
        r.z = newZ;
        r.depth = _snap(Math.max(MIN_ROOM_DIM, Number(r.depth) - dz));
      }
    }
  }

  // 3) Re-attach exterior windows: keep their absolute centre fixed by
  //    rewriting `position` against the new room dimensions.
  if (Array.isArray(floor.windows)) {
    for (const w of floor.windows) {
      if (!w || w.deleted) continue;
      const prev = prevRooms.get(w.room);
      const cur = (floor.rooms || []).find((r) => r && r.id === w.room);
      if (!prev || !cur) continue;
      const side = String(w.side || "front").toLowerCase();
      const pos = Number.isFinite(w.position) ? w.position : 0.5;
      if (side === "front" || side === "rear") {
        // centre runs along x
        const absCentre = prev.x + prev.width * pos;
        const newWidth = Number(cur.width);
        if (newWidth > 0) {
          w.position = _clamp01((absCentre - Number(cur.x)) / newWidth);
        }
      } else {
        // left / right — centre runs along z
        const absCentre = prev.z + prev.depth * pos;
        const newDepth = Number(cur.depth);
        if (newDepth > 0) {
          w.position = _clamp01((absCentre - Number(cur.z)) / newDepth);
        }
      }
    }
  }

  // 4) Re-attach exterior doors stored with `{room_a, room_b:null, side, position}`.
  //    Same math as windows.
  if (Array.isArray(floor.doors)) {
    for (const d of floor.doors) {
      if (!d || d.deleted) continue;
      if (d.is_exterior && d.room_a && Number.isFinite(d.position)) {
        const prev = prevRooms.get(d.room_a);
        const cur = (floor.rooms || []).find((r) => r && r.id === d.room_a);
        if (!prev || !cur) continue;
        const side = String(d.side || "front").toLowerCase();
        const pos = d.position;
        if (side === "front" || side === "rear") {
          const absCentre = prev.x + prev.width * pos;
          const newWidth = Number(cur.width);
          if (newWidth > 0) d.position = _clamp01((absCentre - Number(cur.x)) / newWidth);
        } else {
          const absCentre = prev.z + prev.depth * pos;
          const newDepth = Number(cur.depth);
          if (newDepth > 0) d.position = _clamp01((absCentre - Number(cur.z)) / newDepth);
        }
      }
    }
  }

  // 5) Freeze interior-door positions.
  if (Array.isArray(floor.doors)) {
    const newRooms = new Map();
    for (const r of floor.rooms || []) {
      if (!r || r.deleted === true) continue;
      newRooms.set(r.id, {
        x: Number(r.x), z: Number(r.z),
        width: Number(r.width), depth: Number(r.depth),
      });
    }
    for (const d of floor.doors) {
      if (!d || d.deleted || d.is_exterior) continue;
      if (!d.room_a || !d.room_b) continue;
      const key = [d.room_a, d.room_b].sort().join("|");
      const pre = preDoorCentres.get(key);
      if (!pre) continue;
      const shared = _sharedWallSpan(newRooms.get(d.room_a), newRooms.get(d.room_b));
      if (!shared) continue;
      const newPos = (pre.centre - shared.lo) / Math.max(0.0001, shared.hi - shared.lo);
      d.position = _clamp01(newPos);
    }
  }

  // 6) Phase 4 — opening-width clamps. For every door/window, compute
  //    the post-move host-wall length. If host < width + EDGE_MARGIN,
  //    shrink the width. If even the code minimum no longer fits,
  //    BLOCK the entire drag and return the original blueprint with a
  //    descriptive warning that the caller turns into a toast.
  const warnings = [];
  let blocked = false;

  if (Array.isArray(floor.windows)) {
    for (const w of floor.windows) {
      if (!w || w.deleted) continue;
      const cur = (floor.rooms || []).find((r) => r && r.id === w.room);
      if (!cur) continue;
      const side = String(w.side || "front").toLowerCase();
      const hostLen = side === "front" || side === "rear"
        ? Number(cur.width)
        : Number(cur.depth);
      const width = Number.isFinite(w.width) ? Number(w.width) : 1.2;
      const min = adaMode ? IRC_WINDOW_MIN_M : IRC_WINDOW_MIN_M; // ADA leaves windows alone
      const available = hostLen - EDGE_MARGIN_M;
      if (available < min) {
        blocked = true;
        warnings.push({
          type: "ada_block_window",
          kind: "window",
          id: w.id || null,
          room_name: cur.name,
          min,
        });
      } else if (width > available) {
        w.width = _snap(available);
        warnings.push({
          type: "shrunk_window",
          kind: "window",
          id: w.id || null,
          room_name: cur.name,
          width: w.width,
          min,
        });
      }
    }
  }

  if (Array.isArray(floor.doors)) {
    for (const d of floor.doors) {
      if (!d || d.deleted) continue;
      // host wall length
      let hostLen = 0;
      let hostRoomName;
      if (d.room_a && d.room_b) {
        const ra = (floor.rooms || []).find((r) => r && r.id === d.room_a);
        const rb = (floor.rooms || []).find((r) => r && r.id === d.room_b);
        const shared = _sharedWallSpan(ra, rb);
        if (!shared) continue;
        hostLen = shared.hi - shared.lo;
        hostRoomName = ra?.name || rb?.name;
      } else if (d.room_a) {
        const ra = (floor.rooms || []).find((r) => r && r.id === d.room_a);
        if (!ra) continue;
        const side = String(d.side || "front").toLowerCase();
        hostLen = side === "front" || side === "rear" ? Number(ra.width) : Number(ra.depth);
        hostRoomName = ra.name;
      } else {
        continue;
      }
      const dwidth = Number.isFinite(d.width) ? Number(d.width) : 0.9;
      // ADA picks per-room target; otherwise IRC minimum.
      const codeMin = adaMode
        ? adaDoorTargetWidth(hostRoomName)
        : IRC_DOOR_MIN_M;
      const available = hostLen - EDGE_MARGIN_M;
      if (available < codeMin) {
        blocked = true;
        warnings.push({
          type: adaMode ? "ada_block_door" : "code_block_door",
          kind: "door",
          id: d.id || null,
          room_name: hostRoomName,
          min: codeMin,
        });
      } else if (dwidth > available) {
        d.width = _snap(available);
        warnings.push({
          type: "shrunk_door",
          kind: "door",
          id: d.id || null,
          room_name: hostRoomName,
          width: d.width,
          min: codeMin,
        });
      }
    }
  }

  if (blocked) {
    return { blueprint, warnings, blocked: true };
  }
  return { blueprint: out, warnings, blocked: false };
}

function _clamp01(v) {
  if (!Number.isFinite(v)) return 0.5;
  if (v < 0) return 0;
  if (v > 1) return 1;
  return v;
}

// Returns the shared-wall span between two rectangular rooms, or null.
// Result: { axis: "x"|"z", lo, hi } where the span axis is the wall's
// running direction.
function _sharedWallSpan(a, b) {
  if (!a || !b) return null;
  const TOL = 0.01;
  const ax1 = a.x + a.width, bx1 = b.x + b.width;
  const az1 = a.z + a.depth, bz1 = b.z + b.depth;
  // Constant-x shared wall (vertical wall, span along z)
  for (const [axA, axB] of [[ax1, b.x], [a.x, bx1]]) {
    if (Math.abs(axA - axB) < TOL) {
      const lo = Math.max(a.z, b.z), hi = Math.min(az1, bz1);
      if (hi - lo > TOL) return { axis: "z", lo, hi };
    }
  }
  // Constant-z shared wall (horizontal wall, span along x)
  for (const [azA, azB] of [[az1, b.z], [a.z, bz1]]) {
    if (Math.abs(azA - azB) < TOL) {
      const lo = Math.max(a.x, b.x), hi = Math.min(ax1, bx1);
      if (hi - lo > TOL) return { axis: "x", lo, hi };
    }
  }
  return null;
}

// For every interior-door pair on the floor, capture its pre-move
// absolute centre along the shared-wall span. Returns Map keyed by
// sorted "{room_a}|{room_b}" → { centre, axis }.
function _captureInteriorDoorCentres(floor, prevRooms) {
  const out = new Map();
  const doors = Array.isArray(floor?.doors) ? floor.doors : [];
  for (const d of doors) {
    if (!d || d.deleted || d.is_exterior) continue;
    if (!d.room_a || !d.room_b) continue;
    const a = prevRooms.get(d.room_a);
    const b = prevRooms.get(d.room_b);
    const shared = _sharedWallSpan(a, b);
    if (!shared) continue;
    const pos = Number.isFinite(d.position) ? d.position : 0.5;
    const centre = shared.lo + (shared.hi - shared.lo) * pos;
    out.set([d.room_a, d.room_b].sort().join("|"), { centre, axis: shared.axis });
  }
  return out;
}
